/* eslint-disable camelcase */
import React, { forwardRef } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import Close from '@mui/icons-material/Close';
import Error from '@mui/icons-material/Error';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { AnimatePresence, motion } from 'framer-motion';

import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';
import { Progress } from 'uikit/progress';
import { Loading } from 'uikit/loaders';
import { Tooltip } from 'uikit/tooltips';

import usePreviousState from '../hooks/usePreviousState';

import TemporaryElement from './temporaryElement';

import styles from '../styles/fileListItem.module.scss';

const transformFileSize = (size) => {
  const index = Math.floor(Math.log(size) / Math.log(1024));
  return `${(size / 1024 ** index).toFixed(2)} ${['B', 'KB', 'MB', 'GB', 'TB'][index]}`;
};

const ForwardElement = forwardRef(({ children }, ref) => <span ref={ref}>{children}</span>);

const renderStatus = ({ prevStatus, status }) => {
  if (
    status === 'rag_token_requested' ||
    status === 'request_file_transcript' ||
    status === 'requested'
  ) {
    return (
      <AnimatePresence exitBeforeEnter>
        <Tooltip content={i18n.t('source-material.process')}>
          <ForwardElement>
            <Loading size={16} />
          </ForwardElement>
        </Tooltip>
      </AnimatePresence>
    );
  }

  if (prevStatus && prevStatus !== 'success' && status === 'success') {
    return (
      <AnimatePresence exitBeforeEnter>
        <TemporaryElement delay={5000}>
          <motion.div
            className={styles.successIcon}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            key="success"
          >
            <Tooltip content={i18n.t('source-material.process-success')}>
              <CheckCircle sx={{ height: 16, width: 16 }} />
            </Tooltip>
          </motion.div>
        </TemporaryElement>
      </AnimatePresence>
    );
  }

  if (status === 'failure') {
    return (
      <AnimatePresence exitBeforeEnter>
        <motion.div
          className={styles.failureIcon}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          key="error"
          aria-live="assertive"
          role="alert"
        >
          <Tooltip content={i18n.t('source-material.process-failure')}>
            <Error sx={{ height: 16, width: 16 }} />
          </Tooltip>
        </motion.div>
      </AnimatePresence>
    );
  }

  return null;
};

const FileListItem = ({
  name,
  materialType,
  onRemove,
  fileSize = null,
  image = null,
  status = '',
  upload = null,
  Element = 'li',
}) => {
  const prevStatus = usePreviousState(status);
  const hasFileSize = fileSize && materialType === 'Resource';

  return (
    <Element className={styles.element}>
      {image && <img className={styles.image} src={image.src} alt={image.alt} />}
      <div className={styles.details}>
        <Typo element="span" variant="c01">
          {name}
        </Typo>
        <Typo element="span" variant="s01">
          {hasFileSize ? transformFileSize(fileSize) : materialType}
        </Typo>
      </div>
      {renderStatus({ prevStatus, status })}
      <AnimatePresence exitBeforeEnter>
        {upload?.status === 'idle' || upload?.status === 'uploading' ? (
          <div className={styles.upload}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              key="forms__form-field-upload-actions-uploading"
            >
              <Progress
                value={upload.progress}
                shouldAnimate
                color="primary"
                className={styles.progress}
              />
            </motion.div>
          </div>
        ) : (
          <Button
            variant="ghost"
            icon={<Close />}
            size="xsmall"
            onClick={onRemove}
          />
        )}
      </AnimatePresence>
    </Element>
  );
};

FileListItem.propTypes = {
  Element: PropTypes.elementType,
  materialType: PropTypes.oneOf(['Course', 'Resource']).isRequired,
  name: PropTypes.string.isRequired,
  fileSize: PropTypes.number,
  image: PropTypes.shape({
    src: PropTypes.elementType,
    alt: PropTypes.string,
  }),
  onRemove: PropTypes.func.isRequired,
  status: PropTypes.oneOf([
    '',
    'created',
    'failure',
    'rag_token_requested',
    'request_file_transcript',
    'requested',
    'success',
  ]),
  upload: PropTypes.shape({
    progress: PropTypes.number.isRequired,
    status: PropTypes.oneOf(['idle', 'uploading', 'complete']).isRequired,
  }),
};

export default FileListItem;
